import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BrowserIdService } from '../browser-id.service';

export type ChatStreamEvent =
  | { kind: 'token'; content: string }
  | { kind: 'done'; turn_id: string | null }
  | { kind: 'ticket'; message: string; redirect_url: string | null };

export interface FeedbackResponse {
  feedback_id: string;
  turn_id: string;
  session_id: string;
  rating: 'up' | 'down';
  comment: string | null;
}

export interface BotStatus {
  status: 'online' | 'degraded' | 'unavailable';
  label: string;
  chat_enabled: boolean;
  feedback_enabled: boolean;
  rag_enabled: boolean;
  multilingual_enabled: boolean;
  checked_at: string;
  retry_after_seconds: number;
}

@Injectable({
  providedIn: 'root',
})
export class ChatbotService {
  private static readonly SESSION_STORAGE_KEY = 'chatbot-session-id';
  readonly sessionId$ = new BehaviorSubject<string | null>(
    typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(ChatbotService.SESSION_STORAGE_KEY)
      : null,
  );

  private readonly _botStatus = new BehaviorSubject<BotStatus | null>(null);
  readonly botStatus$ = this._botStatus.asObservable();

  private _statusPollTimer: ReturnType<typeof setInterval> | null = null;

  constructor(private browserIdService: BrowserIdService) {}

  private buildDataHeader(deviceId: string): string {
    const payload = {
      device_id: deviceId,
      platform: 'web',
    };
    const json = JSON.stringify(payload);
    const bytes = new TextEncoder().encode(json);
    return btoa(String.fromCharCode(...bytes));
  }

  private buildHeaders(deviceId: string): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'x-api-key': environment.x_api_key,
      data: this.buildDataHeader(deviceId),
    };
  }

  // ─── Status polling ──────────────────────────────────────────────────────────

  async fetchStatus(): Promise<void> {
    try {
      const res = await fetch(`${environment.chatbotBaseUrl}status`, {
        headers: { 'x-api-key': environment.x_api_key },
      });
      if (res.ok) {
        const status = await res.json() as BotStatus;
        this._botStatus.next(status);
      }
    } catch {
    }
  }

  startStatusPolling(): void {
    this.stopStatusPolling();
    void this.fetchStatus(); // immediate first call
    this._statusPollTimer = setInterval(() => void this.fetchStatus(), 5 * 60_000);
  }

  stopStatusPolling(): void {
    if (this._statusPollTimer !== null) {
      clearInterval(this._statusPollTimer);
      this._statusPollTimer = null;
    }
  }

  chatWithBot(
    message: string,
    signal: AbortSignal,
    sessionId: string | null = null,
    language: string = 'en',
  ): Observable<ChatStreamEvent> {
    const payload: {
      message: string;
      session_id: string | null;
      stream: boolean;
      language: string;
      user_authenticated: boolean;
    } = {
      message,
      session_id: sessionId,
      stream: true,
      language,
      user_authenticated: false,
    };

    return new Observable<ChatStreamEvent>((observer) => {
      firstValueFrom(this.browserIdService.getBrowserId()).then((deviceId) => {
        const headers = this.buildHeaders(deviceId);

        fetch(`${environment.chatbotBaseUrl}chat`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
          signal,
        })
          .then((response) => {
            if (!response.ok) {
              observer.error(`HTTP error: ${response.status}`);
              return;
            }

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let completed = false;

            const handleDataLine = (line: string): boolean => {
              if (!line.startsWith('data:')) return false;
              const raw = line.slice('data:'.length).trim();
              if (!raw) return false;

              try {
                const event = JSON.parse(raw) as {
                  type: string;
                  content?: string;
                  session_id?: string;
                  turn_id?: string;
                  message?: string;
                  redirect_url?: string;
                };

                switch (event.type) {
                  case 'token':
                    if (event.content) observer.next({ kind: 'token', content: event.content });
                    break;
                  case 'session':
                    if (event.session_id) {
                      this.sessionId$.next(event.session_id);
                      try {
                        sessionStorage.setItem(ChatbotService.SESSION_STORAGE_KEY, event.session_id);
                      } catch (_e) {
                        // sessionStorage unavailable in SSR / private-browsing edge cases
                      }
                    }
                    break;
                  case 'ticket':
                    observer.next({
                      kind: 'ticket',
                      message: event.message ?? '',
                      redirect_url: event.redirect_url ?? null,
                    });
                    break;
                  case 'error':
                    // Re-fetch the real status from the API instead of guessing
                    void this.fetchStatus();
                    if (event.message) observer.next({ kind: 'token', content: event.message });
                    break;
                  case 'done':
                    observer.next({ kind: 'done', turn_id: event.turn_id ?? null });
                    return true;
                }
              } catch {
                console.warn('Chatbot: Could not parse SSE data line:', raw);
              }
              return false;
            };

            const readChunk = () => {
              reader?.read()
                .then(({ done, value }) => {
                  if (done) {
                    if (buffer.trim()) handleDataLine(buffer.trim());
                    if (!completed) observer.complete();
                    return;
                  }

                  buffer += decoder.decode(value, { stream: true });

                  const lines = buffer.split('\n');
                  buffer = lines.pop() ?? '';

                  for (const line of lines) {
                    const trimmed = line.trim();
                    if (!trimmed) continue;
                    if (handleDataLine(trimmed)) {
                      completed = true;
                      observer.complete();
                      return;
                    }
                  }

                  readChunk();
                })
                .catch((err: unknown) => {
                  if ((err as DOMException)?.name === 'AbortError') {
                    observer.complete();
                  } else {
                    observer.error(err);
                  }
                });
            };

            readChunk();
          })
          .catch((err: unknown) => {
            if ((err as DOMException)?.name === 'AbortError') {
              observer.complete();
            } else {
              observer.error(err);
            }
          });
      });
    });
  }

  clearSession(): void {
    try {
      sessionStorage.removeItem(ChatbotService.SESSION_STORAGE_KEY);
    } catch (_e) {
    }
    this.sessionId$.next(null);
  }

  
  async submitFeedback(
    turnId: string,
    rating: 'up' | 'down',
    comment: string | null = null,
  ): Promise<FeedbackResponse> {
    const deviceId = await firstValueFrom(this.browserIdService.getBrowserId());
    const headers = this.buildHeaders(deviceId);

    const body: Record<string, unknown> = { turn_id: turnId, rating };
    if (comment) body['comment'] = comment;

    const res = await fetch(`${environment.chatbotBaseUrl}conversations/feedback`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      throw Object.assign(new Error(`Feedback failed: ${res.status}`), { status: res.status });
    }

    return res.json() as Promise<FeedbackResponse>;
  }
}
